import React, { useContext, useEffect, useState } from "react"
import { ConfigProvider, message, Spin } from "antd"
import { Locale } from "antd/lib/locale-provider"
import { Cookies } from "react-cookie"
import { i18n } from "i18next"
import { DefaultLanguage } from "@moodys/cre-cpm.constants.localization"
import { createI18nInstance } from "@moodys/cre-cpm.functions.localization"
import { measures } from "@moodys/cre-cpm.constants.measures"
import {
  authApiWithEnvURLConfig,
  AuthSessionCompany,
  AuthSessionUser,
  Company,
  DynamicLayout,
  FeatureAccess,
  Language,
  Session,
  User as AuthUser
} from "@moodys/cre-cpm-client-apis.apis.services.auth"
import { ApiResponse } from "@moodys/cre-cpm-client-apis.apis.api-base"
import { User } from "@moodys/cre-cpm.models.user"
import { Measure } from "@moodys/cre-cpm-client-apis.apis.services.platform/models/measure.model"
import { isSuperadmin, isUserActive } from "@moodys/cre-cpm-platform-shared.functions.user"
import { redirectTo } from "@moodys/cre-cpm-platform-shared.functions.window-location"
import { CookieNames } from "@moodys/cre-cpm.constants.cookies"
import { MAX_PAGE_SIZE } from "@moodys/cre-cpm-platform-shared.constants.config"
import { platformSharedLocales } from "@moodys/cre-cpm-platform-shared.locales"
import { antdLocales } from "@moodys/cre-cpm-platform-shared.constants.languages"
import { populateDynamicLayouts } from "@moodys/cre-cpm-platform-shared.functions.dashboard"

export interface AuthContextProps {
  companyData: Company | null
  companyRefId: string | null
  currencyCode: string
  dynamicLayouts: DynamicLayout | null
  ensureCookieSyncWithState: () => void
  featureAccess: FeatureAccess[]
  getActiveUsers: () => User[]
  hasFeatureAccess: (feature: FeatureAccess) => boolean
  i18nInstance: i18n
  loadingUsers: boolean
  loadUsers: () => void
  measureBy: Measure
  region: string
  sessionCompany: AuthSessionCompany | null
  sessionUser: AuthSessionUser | null
  token: string
  userData: AuthUser | null
  userLanguage: Language
  users: User[]
}

export interface AuthContextProviderProps {
  children: React.ReactNode
  i18nInstance: i18n
  logoutURL: string
}

export const defaultSharedTranslationInstance = createI18nInstance(
  ["cpm-react-shared"],
  platformSharedLocales
)

export const initialAuthContext: AuthContextProps = {
  companyData: null,
  companyRefId: null,
  currencyCode: "EUR",
  dynamicLayouts: null,
  ensureCookieSyncWithState: () => {},
  featureAccess: [],
  getActiveUsers: () => [],
  hasFeatureAccess: () => false,
  i18nInstance: defaultSharedTranslationInstance,
  loadingUsers: false,
  loadUsers: () => "",
  measureBy: measures.m2,
  region: "eur",
  sessionCompany: null,
  sessionUser: null,
  token: "",
  userData: null,
  userLanguage: DefaultLanguage,
  users: []
}

export const AuthContext = React.createContext(initialAuthContext)

export const useAuthContext = (): AuthContextProps => useContext(AuthContext)

export const AuthContextProvider = ({
  children,
  logoutURL = process.env.REACT_APP_LOGOUT_URL || "/logout",
  i18nInstance
}: AuthContextProviderProps): JSX.Element => {
  const [antDesignLocale, setAntDesignLocale] = useState<Locale>()
  const [companyData, setCompanyData] = useState<Company | null>(null)
  const [companyRefId, setCompanyRefId] = useState<string | null>(null)
  const [currencyCode, setCurrencyCode] = useState<string>(initialAuthContext.currencyCode)
  const [dynamicLayouts, setDynamicLayouts] = useState<DynamicLayout | null>(null)
  const [featureAccess, setFeatureAccess] = useState<FeatureAccess[]>([])
  const [i18nReady, setI18nReady] = useState<boolean>(false)
  const [loadingUsers, setLoadingUsers] = useState<boolean>(false)
  const [measureBy, setMeasureBy] = useState<Measure>(measures.m2)
  const [region, setRegion] = useState<string>(initialAuthContext.region)
  const [sessionCompany, setSessionCompany] = useState<AuthSessionCompany | null>(null)
  const [sessionUser, setSessionUser] = useState<AuthSessionUser | null>(null)
  const [token, setToken] = useState<string>("")
  const [userLanguage, setUserLanguage] = useState<Language>("en")
  const [users, setUsers] = useState<User[]>([])
  const [userData, setUserData] = useState<AuthUser | null>(null)

  const buildSessionData = (session: Session): void => {
    const { user, company } = session.attributes
    if (user) {
      setUserLanguage(user.language ?? DefaultLanguage)
      setMeasureBy(measures[user.measured_by])
      setCurrencyCode(user.currency_code)
      setSessionUser(user)
    }
    if (company) {
      setRegion(company.region)
      setCompanyRefId(company.company_ref_uuid)
      setSessionCompany(company)
    }
  }

  const getCompanyData = (): void => {
    if (companyRefId) {
      authApiWithEnvURLConfig.getCompany(companyRefId).then((response) => {
        if (response.kind === "ok") {
          const { layouts } = response.data.attributes
          setDynamicLayouts(populateDynamicLayouts(layouts))
          setCompanyData(response.data)
        } else {
          message.error(response.kind)
        }
      })
    }
  }

  const getUserData = (id: string): Promise<ApiResponse<AuthUser>> =>
    authApiWithEnvURLConfig.getUser(id)

  const hasFeatureAccess = (feature: FeatureAccess): boolean =>
    featureAccess.includes(feature) || isSuperadmin(sessionUser?.user_type)

  const validateCredentials = (): void => {
    setSessionUser(null)
    const rxdAuthSession = new Cookies().get(CookieNames.rxdAuthSession)
    if (rxdAuthSession) {
      authApiWithEnvURLConfig.getSession().then((response) => {
        if (response.kind === "ok") {
          setFeatureAccess(response.data.attributes.feature_access ?? [])
          buildSessionData(response.data)
          setToken(rxdAuthSession)
        } else {
          redirectTo(logoutURL)
        }
      })
    } else {
      redirectTo(logoutURL)
    }
  }

  useEffect(() => {
    validateCredentials()
  }, [])

  useEffect(() => {
    if (companyRefId) {
      getCompanyData()
    }
  }, [companyRefId])

  useEffect(() => {
    if (sessionUser?.user_id) {
      getUserData(sessionUser.user_id.toString()).then((response) => {
        if (response.kind === "ok") {
          setUserData(response.data)
        }
      })
    }
  }, [sessionUser?.user_id])

  useEffect(() => {
    if (sessionUser && sessionUser.language) {
      setAntDesignLocale(antdLocales[sessionUser.language])
      i18nInstance.changeLanguage(sessionUser.language, () => {
        setI18nReady(true)
      })
    }
  }, [sessionUser])

  const ensureCookieSyncWithState = (): void => {
    if (sessionUser) {
      const rxdAuthSession = new Cookies().get(CookieNames.rxdAuthSession)
      if (!rxdAuthSession) {
        redirectTo(logoutURL)
      }
      if (token && token !== rxdAuthSession) {
        validateCredentials()
      }
    }
  }

  const getActiveUsers = (): User[] => users.filter((filteredUser) => isUserActive(filteredUser))

  const loadUsers = (): void => {
    if (!users.length && companyRefId) {
      setLoadingUsers(true)
      authApiWithEnvURLConfig
        .getUsers({ size: MAX_PAGE_SIZE, number: 1 }, { companyUuid: companyRefId, like: "" })
        .then((response): void => {
          if (response.kind === "ok") {
            setUsers(response.data as User[])
            setLoadingUsers(false)
          }
        })
    }
  }

  return (
    <AuthContext.Provider
      value={{
        companyData,
        companyRefId,
        currencyCode,
        dynamicLayouts,
        ensureCookieSyncWithState,
        featureAccess,
        getActiveUsers,
        hasFeatureAccess,
        i18nInstance,
        loadingUsers,
        loadUsers,
        measureBy,
        region,
        sessionCompany,
        sessionUser,
        token,
        userData,
        userLanguage,
        users
      }}
    >
      <Spin spinning={!sessionUser || !sessionCompany || !i18nReady}>
        {!!sessionUser && !!sessionCompany && i18nReady && (
          <ConfigProvider locale={antDesignLocale}>{children}</ConfigProvider>
        )}
      </Spin>
    </AuthContext.Provider>
  )
}
